import React from 'react';
import { ErrorPlaceholder } from '../../../components/common/ErrorPlaceholder';
import { LoadingPlaceholder } from '../../../components/common/LoadingPlaceholder';
import { Button } from '../../../components/button';
import { BusinessHoursSection } from './components/BusinessHoursSection';
import { BusinessInfoSection } from './components/BusinessInfoSection';
import { BusinessLogoSection } from './components/BusinessLogoSection';
import { SocialMediaSection } from './components/SocialMediaSection';
import { BusinessAddressUIState, BusinessHoursUIState, BusinessProfileUIState } from './BusinessProfile.data';
import { useBusinessProfile } from './hooks/useBusinessProfile';

const BusinessProfilePage: React.FC = () => {
    const { profile, categories, organizations, isLoading, isSaving, error, saveProfile, updateProfileField, addAddress, updateAddress, updateBusinessHours, updateSocialMedia, uploadLogo } = useBusinessProfile();

    const handleSave = () => {
        if (profile) {
            void saveProfile();
        }
    };

    const handleFieldChange = (field: keyof BusinessProfileUIState, value: BusinessProfileUIState[keyof BusinessProfileUIState]) => {
        if (profile) {
            updateProfileField({ [field]: value });
        }
    };

    const handleAddressChange = (index: number, address: BusinessAddressUIState) => {
        updateAddress(index, address);
    };

    const handleAddAddress = () => {
        const newAddress: BusinessAddressUIState = {
            address_1: '',
            city: '',
            state: '',
            zipcode: '',
            latitude: undefined,
            longitude: undefined,
        };
        addAddress(newAddress);
    };

    const handleHoursChange = (day: string, hours: BusinessHoursUIState) => {
        updateBusinessHours(day, hours);
    };

    const handleSocialMediaChange = (field: keyof BusinessProfileUIState['social_media'], value: string) => {
        updateSocialMedia({ [field]: value });
    };

    if (isLoading) {
        return <LoadingPlaceholder />;
    }

    if (error && !profile) {
        return <ErrorPlaceholder error={error} />;
    }

    if (!profile) {
        return null;
    }

    // Use business hours from profile (already initialized in mapper)
    const businessHours = profile.business_hours;

    return (
        <div className="bg-[rgba(255,255,255,0.5)] dark:bg-gray-900/50 min-h-screen p-[24.8px]">
            <div className="max-w-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-[24px] font-bold text-black dark:text-white leading-[32px]">
                        Business Profile
                    </h1>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-[#00c29b] dark:bg-emerald-500 hover:bg-[#00b08a] dark:hover:bg-emerald-600 text-[#004232] dark:text-white h-[40px] px-4 py-[9.6px] rounded-[6px] text-[14px] font-semibold leading-[20px] transition-colors"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>

                {/* Main Content */}
                <div className="space-y-[24px]">
                    {/* Business Logo Section */}
                    <BusinessLogoSection
                        logoUrl={profile.logo_url}
                        onLogoChange={(file) => handleFieldChange('logo_file', file)}
                        onUpload={uploadLogo}
                    />

                    {/* Business Information Section */}
                    <BusinessInfoSection
                        profile={profile}
                        categories={categories}
                        organizations={organizations}
                        onFieldChange={handleFieldChange}
                        onAddressChange={handleAddressChange}
                        onAddAddress={handleAddAddress}
                    />

                    {/* Business Hours and Social Media - Side by Side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px]">
                        <BusinessHoursSection
                            businessHours={businessHours}
                            onHoursChange={handleHoursChange}
                        />

                        <SocialMediaSection
                            socialMedia={profile.social_media}
                            onSocialMediaChange={handleSocialMediaChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessProfilePage;

